import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import careerGrowthService from './careerGrowthService';
import employeeNoteService from './employeeNoteService';
import performanceProfileService, {
    type ProficiencyLevelResponse,
} from './performanceProfileService';

import type { EmployeeEvolution } from './careerGrowthService';
import type { PaginatedResponse } from '@/types/api';
import type {
    CareerAspiration,
    CareerAspirationHistoryEntry,
    CareerAspirationCreateRequest,
    CareerAspirationUpdateRequest,
    CareerStep,
    CareerStepRequest,
    EmployeeGrowth,
} from '@/types/career-growth';
import type {
    EmployeeNote,
    EmployeeNoteCategory,
    EmployeeNoteOriginFilter,
    EmployeeNoteCreateRequest,
    EmployeeNoteUpdateRequest,
} from '@/types/employee-note';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';
import type {
    CompetencyProficiencyHistoryResponse,
    EmployeeCompetencyAssignment,
    Evaluation360Entry,
    ManagerAssessmentEntry,
    PerformanceScore,
    PerformanceTrend,
    SelfAssessmentEntry,
    ProficiencyLevel,
    SkillsetAssessment,
    SkillsetAssessmentFilters,
    PerformanceScoreWindow,
    SkillsetEvaluator,
} from '@/types/performance-profile';

import { PREDEFINED_EVALUATION_PERIOD_NAMES } from '@/api/cycle-evaluation-services/manager-assessment/managerAssessmentService';
import {
    ManagerAssessmentQueryKey,
    PerformanceProfileMutationKey,
    PerformanceProfileQueryKey,
} from '@/constants/QueryKeys';
import { formatDayRange } from '@/utils/dayRange';

/**
 * The employee's performance score, read through the same window the skillset assessment's filters
 * put on their competencies -- the score is their average, so both have to count the same
 * evaluations. The window joins the query key: a period the reader picks is a different question,
 * not a stale answer to the same one.
 */
export const useGetPerformanceScore = (employeeId?: string, window?: PerformanceScoreWindow) => {
    return useQuery<PerformanceScore>({
        queryKey: [PerformanceProfileQueryKey.PERFORMANCE_SCORE, employeeId, window],
        queryFn: () => performanceProfileService.getPerformanceScore(employeeId!, window),
        enabled: !!employeeId,
        // A new window is a new question, so without this the answer disappears while the next one
        // is on its way -- and the profile, which cannot draw itself without a score, collapsed to
        // a loading line, taking the reader's place in the page with it. The previous answer stays
        // on screen instead, marked as no longer the one being asked about. Another employee is
        // not another question about the same person: their score never stands in for this one's,
        // since the page reads the name and the picture beside it from elsewhere.
        placeholderData: (previous, previousQuery) =>
            previousQuery?.queryKey[1] === employeeId ? previous : undefined,
    });
};

export const useGetPerformanceTrend = (employeeId?: string) => {
    return useQuery<PerformanceTrend>({
        queryKey: [PerformanceProfileQueryKey.PERFORMANCE_TREND, employeeId],
        queryFn: () => performanceProfileService.getPerformanceTrend(employeeId!),
        enabled: !!employeeId,
    });
};

/**
 * The periods this employee was evaluated over, for the filter that reads them back. Distinct from
 * `useGetEvaluationPeriods`, which lists the periods a cycle may be *set* to: that vocabulary says
 * what can be written, this one says what exists.
 *
 * This hook is where a period gets its name, and the only place between the service and the two
 * screens that print it: the period picker, and the score dial naming the period it covers.
 */
export const useGetEmployeeEvaluationPeriods = (employeeId?: string) => {
    const { t, i18n } = useTranslation();
    const query = useQuery<EvaluationPeriod[]>({
        queryKey: [PerformanceProfileQueryKey.EVALUATION_PERIODS, employeeId],
        queryFn: () => performanceProfileService.getEmployeeEvaluationPeriods(employeeId!),
        enabled: !!employeeId,
    });

    // One rule, and no case of its own for any id: a period the setup vocabulary named is called by
    // that name, and one it did not is called by the days it spans. Its stored id is not a name --
    // it is how the two ends match each other -- and a name guessed from it would be wrong the day
    // a period runs from March to February, where the dates stay right.
    const data = useMemo(
        () =>
            query.data?.map((period) => ({
                ...period,
                name:
                    period.id in PREDEFINED_EVALUATION_PERIOD_NAMES
                        ? t(period.name as never)
                        : formatDayRange(period.startDate, period.endDate, i18n.language),
            })),
        [query.data, t, i18n.language],
    );

    return { ...query, data };
};

export const useGetEmployeeCompetencyAssignment = (employeeId?: string) => {
    return useQuery<EmployeeCompetencyAssignment>({
        queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT, employeeId],
        queryFn: () => performanceProfileService.getEmployeeCompetencyAssignment(employeeId!),
        enabled: !!employeeId,
    });
};

export const useGetSkillsetAssessment = (
    employeeId?: string,
    filters: SkillsetAssessmentFilters = {},
) => {
    return useQuery<SkillsetAssessment>({
        queryKey: [PerformanceProfileQueryKey.SKILLSET_ASSESSMENT, employeeId, filters],
        queryFn: () => performanceProfileService.getSkillsetAssessment(employeeId!, filters),
        enabled: !!employeeId,
        refetchOnWindowFocus: false,
        staleTime: 0,
    });
};

export const useGetSkillsetEvaluators = (employeeId?: string) => {
    return useQuery<SkillsetEvaluator[]>({
        queryKey: [PerformanceProfileQueryKey.SKILLSET_EVALUATORS, employeeId],
        queryFn: () => performanceProfileService.getSkillsetEvaluators(employeeId!),
        enabled: !!employeeId,
    });
};

export const useUpdateSkillProficiency = (employeeId?: string) => {
    const queryClient = useQueryClient();

    return useMutation<
        void,
        unknown,
        { skillId: string; proficiency: ProficiencyLevel; comment: string; cycleInstanceId: string }
    >({
        mutationKey: [PerformanceProfileMutationKey.UPDATE_SKILL_PROFICIENCY, employeeId],
        mutationFn: ({ skillId, proficiency, comment, cycleInstanceId }) =>
            performanceProfileService.updateSkillProficiency(
                employeeId!,
                skillId,
                proficiency,
                comment,
                cycleInstanceId,
            ),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.SKILLSET_ASSESSMENT, employeeId],
            });
            // The score is the average of these very competencies, and the dial sits right above
            // the list: leaving it out let the two contradict each other on one screen, the
            // corrected level in the list and the score answering from the page's first load.
            // Every window of it, since a correction is not about the period being looked at.
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.PERFORMANCE_SCORE, employeeId],
            });
            // The trend scores each cycle at the levels its list shows, so the corrected cycle's
            // point moves with the dial.
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.PERFORMANCE_TREND, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.SKILLSET_EVALUATORS, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_HISTORY, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PerformanceProfileQueryKey.ASSESSMENT_HISTORY,
                    employeeId,
                    variables.skillId,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, variables.cycleInstanceId],
            });
        },
    });
};

export const useGetAssessmentHistory = (
    employeeId?: string,
    skillId?: string,
    pageNumber: number = 1,
    pageSize: number = 30,
) => {
    return useQuery<PaginatedResponse<CompetencyProficiencyHistoryResponse>>({
        queryKey: [
            PerformanceProfileQueryKey.ASSESSMENT_HISTORY,
            employeeId,
            skillId,
            pageNumber,
            pageSize,
        ],
        queryFn: () =>
            performanceProfileService.getAssessmentHistory(
                employeeId!,
                skillId!,
                pageNumber,
                pageSize,
            ),
        enabled: !!employeeId && !!skillId,
    });
};

export const useAddAdditionalCompetencies = (employeeId?: string) => {
    const queryClient = useQueryClient();

    return useMutation<EmployeeCompetencyAssignment, unknown, string[]>({
        mutationKey: [PerformanceProfileMutationKey.ADD_ADDITIONAL_COMPETENCIES, employeeId],
        mutationFn: (competencyIds: string[]) =>
            performanceProfileService.addAdditionalCompetencies(employeeId!, competencyIds),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.AVAILABLE_COMPETENCIES, employeeId],
            });
        },
    });
};

export const useRemoveAdditionalCompetency = (employeeId?: string) => {
    const queryClient = useQueryClient();

    return useMutation<EmployeeCompetencyAssignment, unknown, string>({
        mutationKey: [PerformanceProfileMutationKey.REMOVE_ADDITIONAL_COMPETENCY, employeeId],
        mutationFn: (competencyId: string) =>
            performanceProfileService.removeAdditionalCompetency(employeeId!, competencyId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.AVAILABLE_COMPETENCIES, employeeId],
            });
        },
    });
};

export const useAddExcludedCompetency = (employeeId?: string) => {
    const queryClient = useQueryClient();

    return useMutation<EmployeeCompetencyAssignment, unknown, string>({
        mutationKey: [PerformanceProfileMutationKey.ADD_EXCLUDED_COMPETENCY, employeeId],
        mutationFn: (competencyId: string) =>
            performanceProfileService.addExcludedCompetency(employeeId!, competencyId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT, employeeId],
            });
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.SKILLSET_ASSESSMENT, employeeId],
            });
        },
    });
};

export const useRemoveExcludedCompetency = (employeeId?: string) => {
    const queryClient = useQueryClient();

    return useMutation<EmployeeCompetencyAssignment, unknown, string>({
        mutationKey: [PerformanceProfileMutationKey.REMOVE_EXCLUDED_COMPETENCY, employeeId],
        mutationFn: (competencyId: string) =>
            performanceProfileService.removeExcludedCompetency(employeeId!, competencyId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.COMPETENCY_ASSIGNMENT, employeeId],
            });
        },
    });
};

export const useGetEvaluation360 = (employeeId?: string) => {
    return useQuery<Evaluation360Entry[]>({
        queryKey: [PerformanceProfileQueryKey.EVALUATION_360, employeeId],
        queryFn: () => performanceProfileService.getEvaluation360(employeeId!),
        enabled: !!employeeId,
    });
};

export const useGetManagerAssessments = (employeeId?: string) => {
    return useQuery<ManagerAssessmentEntry[]>({
        queryKey: [PerformanceProfileQueryKey.MANAGER_ASSESSMENTS, employeeId],
        queryFn: () => performanceProfileService.getManagerAssessments(employeeId!),
        enabled: !!employeeId,
    });
};

export const useGetSelfAssessments = (employeeId?: string) => {
    return useQuery<SelfAssessmentEntry[]>({
        queryKey: [PerformanceProfileQueryKey.SELF_ASSESSMENTS, employeeId],
        queryFn: () => performanceProfileService.getSelfAssessments(employeeId!),
        enabled: !!employeeId,
    });
};

export const useGetProficiencyLevels = () => {
    return useQuery<ProficiencyLevelResponse[]>({
        queryKey: [PerformanceProfileQueryKey.PROFICIENCY_LEVELS],
        queryFn: () => performanceProfileService.getProficiencyLevels(),
    });
};

export const useGetSkillProficiency = (cycleInstanceId?: string, competencyId?: string) => {
    return useQuery<string | null>({
        queryKey: [PerformanceProfileQueryKey.SKILL_PROFICIENCY, cycleInstanceId, competencyId],
        queryFn: () =>
            performanceProfileService.getSkillProficiency(cycleInstanceId!, competencyId!),
        enabled: !!cycleInstanceId && !!competencyId,
    });
};

export const useGetCompetencyProficiencyHistory = (
    employeeId?: string,
    competencyId?: string,
    pageNumber: number = 1,
    pageSize: number = 30,
) => {
    return useQuery<PaginatedResponse<CompetencyProficiencyHistoryResponse>>({
        queryKey: [
            PerformanceProfileQueryKey.COMPETENCY_HISTORY,
            employeeId,
            competencyId,
            pageNumber,
            pageSize,
        ],
        queryFn: () =>
            performanceProfileService.getCompetencyProficiencyHistory(
                employeeId!,
                competencyId!,
                pageNumber,
                pageSize,
            ),
        enabled: !!employeeId && !!competencyId,
    });
};

// --- "Croissance" tab -------------------------------------------------------------------------

export const useGetEmployeeGrowth = (employeeId?: string) => {
    return useQuery<EmployeeGrowth>({
        queryKey: [PerformanceProfileQueryKey.GROWTH, employeeId],
        queryFn: () => careerGrowthService.getEmployeeGrowth(employeeId!),
        enabled: !!employeeId,
    });
};

/**
 * The evolution journey the employee portal draws: the signed-in employee's own, or -- for a
 * manager reading an employee's portal -- that employee's, exactly as they read it themselves.
 */
export const useGetMyEvolution = (employeeId?: string) => {
    return useQuery<EmployeeEvolution>({
        queryKey: [PerformanceProfileQueryKey.MY_EVOLUTION, employeeId],
        queryFn: () => careerGrowthService.getMyEvolution(employeeId),
    });
};

export const useGetCareerAspiration = (employeeId?: string, aspirationId?: string) => {
    return useQuery<CareerAspiration>({
        queryKey: [PerformanceProfileQueryKey.CAREER_ASPIRATION, employeeId, aspirationId],
        queryFn: () => careerGrowthService.getCareerAspiration(employeeId!, aspirationId!),
        enabled: !!employeeId && !!aspirationId,
    });
};

/**
 * The employee's recorded aspirations and the career meetings still waiting for one, without the
 * rest of the growth aggregate: a career meeting's plan finds its own aspiration here. Keyed under
 * the growth read model, so every write that refreshes the tab refreshes this list as well.
 */
export const useGetCareerAspirationHistory = (employeeId?: string) => {
    return useQuery<CareerAspirationHistoryEntry[]>({
        queryKey: [
            PerformanceProfileQueryKey.GROWTH,
            employeeId,
            PerformanceProfileQueryKey.CAREER_ASPIRATION_HISTORY,
        ],
        queryFn: () => careerGrowthService.getCareerAspirationHistory(employeeId!),
        enabled: !!employeeId,
    });
};

/** Every write on the tab refreshes its one read model. */
const useInvalidateGrowth = (employeeId?: string) => {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({
            queryKey: [PerformanceProfileQueryKey.GROWTH, employeeId],
        });
};

export const useCreateCareerStep = (employeeId?: string) => {
    const invalidateGrowth = useInvalidateGrowth(employeeId);

    return useMutation<CareerStep, Error, CareerStepRequest>({
        mutationKey: [PerformanceProfileMutationKey.CREATE_CAREER_STEP, employeeId],
        mutationFn: (request) => careerGrowthService.createCareerStep(employeeId!, request),
        onSuccess: invalidateGrowth,
    });
};

export const useUpdateCareerStep = (employeeId?: string) => {
    const invalidateGrowth = useInvalidateGrowth(employeeId);

    return useMutation<CareerStep, Error, { stepId: string; request: CareerStepRequest }>({
        mutationKey: [PerformanceProfileMutationKey.UPDATE_CAREER_STEP, employeeId],
        mutationFn: ({ stepId, request }) =>
            careerGrowthService.updateCareerStep(employeeId!, stepId, request),
        onSuccess: invalidateGrowth,
    });
};

export const useDeleteCareerStep = (employeeId?: string) => {
    const invalidateGrowth = useInvalidateGrowth(employeeId);

    return useMutation<void, Error, string>({
        mutationKey: [PerformanceProfileMutationKey.DELETE_CAREER_STEP, employeeId],
        mutationFn: (stepId) => careerGrowthService.deleteCareerStep(employeeId!, stepId),
        onSuccess: invalidateGrowth,
    });
};

export const useCreateCareerAspiration = (employeeId?: string) => {
    const queryClient = useQueryClient();
    const invalidateGrowth = useInvalidateGrowth(employeeId);

    return useMutation<CareerAspiration, Error, CareerAspirationCreateRequest>({
        mutationKey: [PerformanceProfileMutationKey.CREATE_CAREER_ASPIRATION, employeeId],
        mutationFn: (request) => careerGrowthService.createCareerAspiration(employeeId!, request),
        onSuccess: (created) => {
            // The answers just written are the answers to read: a screen that finds its aspiration
            // through the refreshed history shows them at once instead of loading them back.
            queryClient.setQueryData(
                [PerformanceProfileQueryKey.CAREER_ASPIRATION, employeeId, created.id],
                created,
            );
            return invalidateGrowth();
        },
    });
};

export const useUpdateCareerAspiration = (employeeId?: string) => {
    const queryClient = useQueryClient();
    const invalidateGrowth = useInvalidateGrowth(employeeId);

    return useMutation<
        CareerAspiration,
        Error,
        { aspirationId: string; request: CareerAspirationUpdateRequest }
    >({
        mutationKey: [PerformanceProfileMutationKey.UPDATE_CAREER_ASPIRATION, employeeId],
        mutationFn: ({ aspirationId, request }) =>
            careerGrowthService.updateCareerAspiration(employeeId!, aspirationId, request),
        onSuccess: (_, { aspirationId }) => {
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.CAREER_ASPIRATION, employeeId, aspirationId],
            });
            return invalidateGrowth();
        },
    });
};

/** A page of notes another query already fetched (the growth aggregate), and when it did. */
export interface EmployeeNotesSeed {
    page: PaginatedResponse<EmployeeNote>;
    updatedAt: number;
}

/** How long a page of notes is trusted before the route is asked again on a remount. */
const EMPLOYEE_NOTES_STALE_TIME = 60_000;

/**
 * The employee's notes, one folder (`category`) or every folder (`null`), paged by the server.
 * `origin` narrows to the notes written in one meeting or during one manager assessment -- each
 * reads its own; the profile tabs never pass it, so they never filter on where a note came from.
 */
export const useGetEmployeeNotes = ({
    employeeId,
    category,
    pageNumber,
    pageSize,
    seed,
    origin = {},
}: {
    employeeId: string | undefined;
    category: EmployeeNoteCategory | null;
    pageNumber: number;
    pageSize: number;
    seed?: EmployeeNotesSeed;
    origin?: EmployeeNoteOriginFilter;
}) => {
    const { thematicMeetingId, cycleInstanceId } = origin;
    // The seed only stands for the page it is, and never for a read narrowed to an origin.
    const isSeededPage =
        !!seed &&
        !thematicMeetingId &&
        !cycleInstanceId &&
        seed.page.pageNumber === pageNumber &&
        seed.page.pageSize === pageSize;

    return useQuery<PaginatedResponse<EmployeeNote>>({
        queryKey: [
            PerformanceProfileQueryKey.EMPLOYEE_NOTES,
            employeeId,
            category,
            pageNumber,
            pageSize,
            thematicMeetingId,
            cycleInstanceId,
        ],
        queryFn: () =>
            employeeNoteService.getEmployeeNotes(employeeId!, category, pageNumber, pageSize, {
                thematicMeetingId,
                cycleInstanceId,
            }),
        enabled: !!employeeId,
        placeholderData: (previous) => previous,
        initialData: isSeededPage ? seed.page : undefined,
        initialDataUpdatedAt: isSeededPage ? seed.updatedAt : undefined,
        staleTime: EMPLOYEE_NOTES_STALE_TIME,
    });
};

/** Every write refreshes every folder and page of the employee's notes, plus the growth aggregate. */
const useInvalidateEmployeeNotes = (employeeId?: string) => {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.EMPLOYEE_NOTES, employeeId],
            }),
            queryClient.invalidateQueries({
                queryKey: [PerformanceProfileQueryKey.GROWTH, employeeId],
            }),
        ]);
};

export const useCreateEmployeeNote = (employeeId?: string) => {
    const invalidateNotes = useInvalidateEmployeeNotes(employeeId);

    return useMutation<EmployeeNote, Error, EmployeeNoteCreateRequest>({
        mutationKey: [PerformanceProfileMutationKey.CREATE_EMPLOYEE_NOTE, employeeId],
        mutationFn: (request) => employeeNoteService.createEmployeeNote(employeeId!, request),
        onSuccess: invalidateNotes,
    });
};

export const useUpdateEmployeeNote = (employeeId?: string) => {
    const invalidateNotes = useInvalidateEmployeeNotes(employeeId);

    return useMutation<EmployeeNote, Error, { noteId: string; request: EmployeeNoteUpdateRequest }>(
        {
            mutationKey: [PerformanceProfileMutationKey.UPDATE_EMPLOYEE_NOTE, employeeId],
            mutationFn: ({ noteId, request }) =>
                employeeNoteService.updateEmployeeNote(employeeId!, noteId, request),
            onSuccess: invalidateNotes,
        },
    );
};

export const useDeleteEmployeeNote = (employeeId?: string) => {
    const invalidateNotes = useInvalidateEmployeeNotes(employeeId);

    return useMutation<void, Error, string>({
        mutationKey: [PerformanceProfileMutationKey.DELETE_EMPLOYEE_NOTE, employeeId],
        mutationFn: (noteId) => employeeNoteService.deleteEmployeeNote(employeeId!, noteId),
        onSuccess: invalidateNotes,
    });
};
